import { useState, useEffect } from 'react';
import { Mail, Send, Eye, X, Filter, Users, AlertTriangle, CheckCircle2, Sparkles, Bell, Crown, Heart, TrendingUp, Zap, Award, Gift, Star, Clock, AlertCircle } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { devError } from '../utils/logger';
import { fetchWithAuth } from '../utils/api';
import { useAdminModalZIndex } from './AdminModalZContext';


interface AdminBulkEmailSenderProps {
  accessToken: string;
}

interface EmailListUser {
  id: string;
  name: string;
  email: string;
  userType: 'talent' | 'employer';
  subscription: 'free' | 'plus' | 'employer' | 'employer_plus';
  marketingEmailsConsent: boolean;
  county?: string;
}

// Icon options (same as AdminAnnouncements)
const ICON_OPTIONS = [
  { value: 'mail', label: 'Mail', component: Mail },
  { value: 'sparkles', label: 'Sparkles', component: Sparkles },
  { value: 'bell', label: 'Bell', component: Bell },
  { value: 'crown', label: 'Crown', component: Crown },
  { value: 'heart', label: 'Heart', component: Heart },
  { value: 'trending', label: 'Trending Up', component: TrendingUp },
  { value: 'zap', label: 'Zap', component: Zap },
  { value: 'award', label: 'Award', component: Award },
  { value: 'gift', label: 'Gift', component: Gift },
  { value: 'star', label: 'Star', component: Star },
  { value: 'clock', label: 'Clock', component: Clock },
  { value: 'alert', label: 'Alert', component: AlertCircle },
];

// Icon to SVG mapping for email HTML
const getIconSVG = (iconValue: string, color: string = '#ffffff'): string => {
  const svgMap: Record<string, string> = {
    mail: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
      <polyline points="22,6 12,13 2,6"></polyline>
    </svg>`,
    sparkles: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <path d="M12 3v18M3 12h18M6 6l12 12M6 18L18 6"></path>
    </svg>`,
    bell: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
    </svg>`,
    crown: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <path d="M2 8l2-3 3 3 5-5 5 5 3-3 2 3v10H2V8z"></path>
    </svg>`,
    heart: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>`,
    trending: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
      <polyline points="17 6 23 6 23 12"></polyline>
    </svg>`,
    zap: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
    </svg>`,
    award: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <circle cx="12" cy="8" r="7"></circle>
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
    </svg>`,
    gift: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <polyline points="20 12 20 22 4 22 4 12"></polyline>
      <rect x="2" y="7" width="20" height="5"></rect>
      <line x1="12" y1="22" x2="12" y2="7"></line>
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
    </svg>`,
    star: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
    </svg>`,
    clock: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>`,
    alert: `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display: inline-block;">
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="8" x2="12" y2="12"></line>
      <line x1="12" y1="16" x2="12.01" y2="16"></line>
    </svg>`,
  };
  return svgMap[iconValue] || svgMap.mail;
};

export function AdminBulkEmailSender({ accessToken }: AdminBulkEmailSenderProps) {
  const adminModalZ = useAdminModalZIndex();
  const [users, setUsers] = useState<EmailListUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Filter states (same as AdminEmailLists)
  const [consentFilter, setConsentFilter] = useState<'all' | 'consented' | 'not_consented'>('consented');
  const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'talent' | 'employer'>('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState<'all' | 'free' | 'plus' | 'employer' | 'employer_plus'>('all');
  const [countyFilter, setCountyFilter] = useState<string>('all');
  
  // Email composition
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [includeUnsubscribe, setIncludeUnsubscribe] = useState(true);
  
  // NEW: Styling options
  const [selectedIcon, setSelectedIcon] = useState('mail');
  const [centerIcon, setCenterIcon] = useState(true);
  const [centerTitle, setCenterTitle] = useState(true);
  const [centerContent, setCenterContent] = useState(false);
  
  // NEW: CTA Button options
  const [showButton, setShowButton] = useState(true);
  const [buttonText, setButtonText] = useState('Gå til Talentfunn');
  const [buttonUrl, setButtonUrl] = useState('https://talentfunn.no');
  
  // Preview modal
  const [showPreview, setShowPreview] = useState(false);
  
  // NEW: Confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmWarning, setConfirmWarning] = useState<string | null>(null);

  const norwegianCounties = [
    'Agder', 'Akershus', 'Buskerud', 'Finnmark', 'Innlandet',
    'Møre og Romsdal', 'Nordland', 'Oslo', 'Rogaland', 'Telemark',
    'Troms', 'Trøndelag', 'Vestfold', 'Vestland', 'Østfold'
  ];

  useEffect(() => {
    fetchEmailLists();
  }, []);

  const fetchEmailLists = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/email-lists`,
        {
          headers: {},
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        toast.error('Kunne ikke laste e-postlister');
      }
    } catch (error) {
      devError('Error fetching email lists:', error);
      toast.error('Noe gikk galt');
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on filters
  const filteredUsers = users.filter(user => {
    // Consent filter
    if (consentFilter === 'consented' && !user.marketingEmailsConsent) return false;
    if (consentFilter === 'not_consented' && user.marketingEmailsConsent) return false;

    // User type filter
    if (userTypeFilter !== 'all' && user.userType !== userTypeFilter) return false;

    // Subscription filter
    if (subscriptionFilter !== 'all' && user.subscription !== subscriptionFilter) return false;

    // County filter
    if (countyFilter !== 'all' && user.county !== countyFilter) return false;

    return true;
  });

  const handleSendBulkEmail = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Emne og melding må fylles ut');
      return;
    }

    if (filteredUsers.length === 0) {
      toast.error('Ingen brukere matcher filtrene');
      return;
    }

    // Show confirmation modal with warning if needed
    if (consentFilter !== 'consented') {
      setConfirmWarning('ADVARSEL: Du sender til brukere som IKKE har samtykket til markedsføring. Dette bør kun gjøres for viktige systemvarsler, ikke markedsføring.');
    } else {
      setConfirmWarning(null);
    }
    setShowConfirmModal(true);
  };

  const confirmAndSend = async () => {
    setShowConfirmModal(false);
    setSending(true);
    try {
      const filters: any = {};
      if (userTypeFilter !== 'all') filters.user_type = userTypeFilter;
      if (subscriptionFilter !== 'all') filters.subscription = subscriptionFilter;
      if (countyFilter !== 'all') filters.fylke = countyFilter;

      const response = await fetchWithAuth(
        `${API_BASE}/admin/send-bulk-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            filters,
            subject,
            message,
            include_unsubscribe: includeUnsubscribe
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`✅ E-post sendt til ${data.emailsSent} av ${data.count} brukere`);
        // Reset form
        setSubject('');
        setMessage('');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Kunne ikke sende e-post');
      }
    } catch (error) {
      devError('Error sending bulk email:', error);
      toast.error('Noe gikk galt');
    } finally {
      setSending(false);
    }
  };

  const renderEmailPreview = () => {
    const SelectedIconComponent = ICON_OPTIONS.find(opt => opt.value === selectedIcon)?.component || Mail;
    const iconAlignment = centerIcon ? 'center' : 'left';
    const titleAlignment = centerTitle ? 'center' : 'left';
    const contentAlignment = centerContent ? 'center' : 'left';
    
    return (
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        backgroundColor: '#ffffff'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#9333ea',
          padding: '40px 24px',
          textAlign: titleAlignment as any,
          borderRadius: '12px 12px 0 0'
        }}>
          <div style={{ 
            marginBottom: '16px',
            display: 'flex',
            justifyContent: iconAlignment,
            alignItems: 'center'
          }}>
            <SelectedIconComponent className="w-10 h-10 text-white" />
          </div>
          <h1 style={{
            color: '#ffffff',
            fontSize: '24px',
            fontWeight: 'bold',
            margin: 0,
            textAlign: titleAlignment as any
          }}>
            {subject || 'Emne her'}
          </h1>
        </div>

        {/* Body */}
        <div style={{
          padding: '32px 24px',
          backgroundColor: '#ffffff'
        }}>
          <p style={{
            margin: '0 0 16px 0',
            color: '#111827',
            fontSize: '18px',
            fontWeight: '600',
            textAlign: contentAlignment as any
          }}>
            Hei [Brukernavn],
          </p>
          
          <div style={{
            margin: '0 0 24px 0',
            color: '#4b5563',
            fontSize: '15px',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap',
            textAlign: contentAlignment as any
          }}>
            {message || 'Din melding her...'}
          </div>

          {/* CTA Button */}
          {showButton && (
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <a href={buttonUrl} style={{
                display: 'inline-block',
                padding: '14px 28px',
                backgroundColor: '#9333ea',
                color: '#ffffff',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '15px'
              }}>
                {buttonText}
              </a>
            </div>
          )}

          {/* Unsubscribe footer */}
          {includeUnsubscribe && (
            <div style={{
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: '#9ca3af',
                textAlign: 'center',
                lineHeight: '1.6'
              }}>
                Vil du ikke lenger motta markedsførings-e-poster fra oss?<br />
                <a href="#" style={{ color: '#9333ea', textDecoration: 'underline' }}>
                  Meld deg av her
                </a>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 30px',
          backgroundColor: '#f9fafb',
          textAlign: 'center',
          borderRadius: '0 0 12px 12px',
          borderTop: '1px solid #e5e7eb'
        }}>
          <p style={{ margin: '0 0 8px 0', color: '#111827', fontSize: '14px' }}>
            Med vennlig hilsen, <strong>Talentfunn</strong>
          </p>
          <p style={{ margin: 0, color: '#9ca3af', fontSize: '12px' }}>
            © 2026 Talentfunn
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader size="lg" className="text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-purple-600" />
            Bulk E-post
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Send e-post til flere brukere samtidig
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">Filtre</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Consent Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Markedsføring samtykke
            </label>
            <select
              value={consentFilter}
              onChange={(e) => setConsentFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">Alle</option>
              <option value="consented">Kun med samtykke</option>
              <option value="not_consented">Uten samtykke</option>
            </select>
          </div>

          {/* User Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brukertype
            </label>
            <select
              value={userTypeFilter}
              onChange={(e) => setUserTypeFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">Alle</option>
              <option value="talent">Talent</option>
              <option value="employer">Arbeidsgiver</option>
            </select>
          </div>

          {/* Subscription Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Abonnement
            </label>
            <select
              value={subscriptionFilter}
              onChange={(e) => setSubscriptionFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">Alle</option>
              <option value="free">Gratis</option>
              <option value="plus">Talentfunn Pluss</option>
              <option value="employer">Arbeidsgiver Basis</option>
              <option value="employer_plus">Arbeidsgiver Pluss</option>
            </select>
          </div>

          {/* County Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fylke
            </label>
            <select
              value={countyFilter}
              onChange={(e) => setCountyFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">Alle fylker</option>
              {norwegianCounties.map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter Stats */}
        <div className="mt-4 flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
          <Users className="w-5 h-5 text-purple-600" />
          <span className="font-semibold text-purple-900">
            {filteredUsers.length} brukere
          </span>
          <span className="text-purple-700">
            vil motta denne e-posten
          </span>
        </div>

        {/* Warning if sending without consent */}
        {consentFilter !== 'consented' && filteredUsers.length > 0 && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900">
                ADVARSEL: Du sender til brukere uten markedsførings-samtykke
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Dette bør kun gjøres for viktige systemvarsler, ikke markedsføring
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Email Composition */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Skriv e-post</h3>

        {/* Subject */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Emne *
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="F.eks: Nye funksjoner på Talentfunn"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {/* Message */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Melding *
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Skriv din melding her..."
            rows={8}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          />
          <p className="text-xs text-gray-500 mt-2">
            Tips: Brukernes navn vil automatisk bli satt inn som "Hei [Navn],"
          </p>
        </div>

        {/* NEW: Styling Options */}
        <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h4 className="font-semibold text-gray-900 mb-3 text-sm">🎨 E-post Design</h4>
          
          <div className="space-y-4">
            {/* Icon Selection Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ikon
                </label>
                <select
                  value={selectedIcon}
                  onChange={(e) => setSelectedIcon(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                >
                  {ICON_OPTIONS.map(icon => (
                    <option key={icon.value} value={icon.value}>
                      {icon.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Alignment Checkboxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Justering
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={centerIcon}
                    onChange={(e) => setCenterIcon(e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Midtstill ikon</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={centerTitle}
                    onChange={(e) => setCenterTitle(e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Midtstill emne/tittel</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={centerContent}
                    onChange={(e) => setCenterContent(e.target.checked)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">Midtstill innhold/tekst</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* NEW: CTA Button Options */}
        <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 text-sm">🔘 CTA Knapp</h4>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showButton}
                onChange={(e) => setShowButton(e.target.checked)}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Vis knapp</span>
            </label>
          </div>
          
          {showButton && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-gray-200">
              {/* Button Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Knapp Tekst
                </label>
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                  placeholder="F.eks: Gå til Talentfunn"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Button URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Knapp URL
                </label>
                <input
                  type="text"
                  value={buttonUrl}
                  onChange={(e) => setButtonUrl(e.target.value)}
                  placeholder="F.eks: https://talentfunn.no"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Include Unsubscribe Checkbox */}
        <div className="mb-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeUnsubscribe}
              onChange={(e) => setIncludeUnsubscribe(e.target.checked)}
              className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">
                Inkluder "Meld deg av"-link
              </span>
              <p className="text-xs text-gray-600 mt-1">
                Må være aktivert for markedsførings-e-poster (GDPR-krav)
              </p>
            </div>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => setShowPreview(true)}
            disabled={!subject.trim() || !message.trim()}
            className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Eye className="w-5 h-5" />
            Forhåndsvis
          </button>

          <button
            onClick={handleSendBulkEmail}
            disabled={!subject.trim() || !message.trim() || sending || filteredUsers.length === 0}
            className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {sending ? (
              <>
                <Loader size="md" />
                Sender...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Send til {filteredUsers.length} brukere
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: adminModalZ ?? 100 }}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Forhåndsvisning av e-post
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {renderEmailPreview()}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end gap-3">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Lukk
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  handleSendBulkEmail();
                }}
                disabled={sending || filteredUsers.length === 0}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Send nå
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: adminModalZ ?? 100 }}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Bekreft sending av e-post
              </h3>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {confirmWarning && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900">
                      {confirmWarning}
                    </p>
                  </div>
                </div>
              )}
              <p className="text-sm text-gray-700">
                Er du sikker på at du vil sende denne e-posten til {filteredUsers.length} brukere?
              </p>
              <p className="text-sm text-gray-700 mt-1">
                Emne: {subject}
              </p>
              <p className="text-sm text-gray-700 mt-1">
                Unsubscribe-link: {includeUnsubscribe ? 'Ja' : 'Nei'}
              </p>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={confirmAndSend}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
